import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { AvailabilitySlot } from "@/lib/types";

interface AvailabilityCalendarProps {
	availability: AvailabilitySlot[];
	selectedStart?: string;
	selectedEnd?: string;
	onSelectRange?: (start: string, end: string) => void;
	readOnly?: boolean;
}

export function AvailabilityCalendar({
	availability,
	selectedStart,
	selectedEnd,
	onSelectRange,
	readOnly = false,
}: AvailabilityCalendarProps) {
	const [currentMonth, setCurrentMonth] = useState(() => {
		const now = new Date();
		return { year: now.getFullYear(), month: now.getMonth() };
	});
	const [selectingStart, setSelectingStart] = useState<string | null>(null);

	const availMap = new Map<string, boolean>();
	availability.forEach((s) => availMap.set(s.date, s.available));

	const daysInMonth = new Date(
		currentMonth.year,
		currentMonth.month + 1,
		0
	).getDate();
	const firstDayOfWeek = new Date(
		currentMonth.year,
		currentMonth.month,
		1
	).getDay();

	const monthName = new Date(
		currentMonth.year,
		currentMonth.month
	).toLocaleString("default", { month: "long", year: "numeric" });

	const days: Array<{ date: string; day: number; available: boolean; inRange: boolean; isStart: boolean; isEnd: boolean; isPast: boolean }> = [];
	const today = new Date().toISOString().split("T")[0];

	for (let d = 1; d <= daysInMonth; d++) {
		const date = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
		const available = availMap.get(date) ?? true;
		const isPast = date < today;

		let inRange = false;
		let isStart = false;
		let isEnd = false;

		const effectiveStart = selectingStart || selectedStart;
		const effectiveEnd = selectedEnd;

		if (effectiveStart && effectiveEnd) {
			inRange = date >= effectiveStart && date <= effectiveEnd;
			isStart = date === effectiveStart;
			isEnd = date === effectiveEnd;
		} else if (effectiveStart) {
			isStart = date === effectiveStart;
			inRange = isStart;
		}

		days.push({ date, day: d, available, inRange, isStart, isEnd, isPast });
	}

	function handleDayClick(date: string, available: boolean) {
		if (readOnly || !available) return;

		if (!selectingStart) {
			setSelectingStart(date);
			onSelectRange?.(date, date);
		} else {
			if (date < selectingStart) {
				setSelectingStart(date);
				onSelectRange?.(date, date);
			} else {
				onSelectRange?.(selectingStart, date);
				setSelectingStart(null);
			}
		}
	}

	function prevMonth() {
		setCurrentMonth((m) => {
			if (m.month === 0) return { year: m.year - 1, month: 11 };
			return { ...m, month: m.month - 1 };
		});
	}

	function nextMonth() {
		setCurrentMonth((m) => {
			if (m.month === 11) return { year: m.year + 1, month: 0 };
			return { ...m, month: m.month + 1 };
		});
	}

	return (
		<div className="w-full">
			<div className="flex items-center justify-between mb-3">
				<Button variant="ghost" size="sm" onClick={prevMonth}>
					<ChevronLeft className="w-4 h-4" />
				</Button>
				<span className="text-sm font-medium">{monthName}</span>
				<Button variant="ghost" size="sm" onClick={nextMonth}>
					<ChevronRight className="w-4 h-4" />
				</Button>
			</div>

			<div className="grid grid-cols-7 gap-0.5 mb-1">
				{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
					<div
						key={d}
						className="text-center text-[10px] font-medium text-muted-foreground py-1"
					>
						{d}
					</div>
				))}
			</div>

			<div className="grid grid-cols-7 gap-0.5">
				{Array.from({ length: firstDayOfWeek }).map((_, i) => (
					<div key={`empty-${i}`} />
				))}
				{days.map((day) => (
					<button
						key={day.date}
						disabled={!day.available || day.isPast || readOnly}
						onClick={() => handleDayClick(day.date, day.available)}
						className={`
							aspect-square rounded-md text-xs flex items-center justify-center transition-all
							${day.isPast ? "text-muted-foreground/40 cursor-default" : ""}
							${!day.available && !day.isPast ? "bg-muted/50 text-muted-foreground/50 line-through cursor-not-allowed" : ""}
							${day.available && !day.isPast && !day.inRange ? "hover:bg-primary/10 cursor-pointer" : ""}
							${day.inRange && !day.isStart && !day.isEnd ? "bg-primary/10 text-primary" : ""}
							${day.isStart ? "bg-primary text-primary-foreground rounded-r-none" : ""}
							${day.isEnd ? "bg-primary text-primary-foreground rounded-l-none" : ""}
							${day.isStart && day.isEnd ? "rounded-md" : ""}
						`}
					>
						{day.day}
					</button>
				))}
			</div>

			{!readOnly && (
				<div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
					<div className="flex items-center gap-1">
						<div className="w-3 h-3 rounded-sm bg-primary/10 border border-primary/20" />
						Available
					</div>
					<div className="flex items-center gap-1">
						<div className="w-3 h-3 rounded-sm bg-muted/50 border border-border" />
						Unavailable
					</div>
					<div className="flex items-center gap-1">
						<div className="w-3 h-3 rounded-sm bg-primary" />
						Selected
					</div>
				</div>
			)}
		</div>
	);
}
